import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/axiosInstance';
import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
import { Grid, AppBar, Button, Typography, Card, CardContent, CardMedia, IconButton, 
    CircularProgress, CardActions, CardHeader, TextField } from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
import OfficeIconPng from './Assets/Mapicons/office.png';
import HouseIconPng from './Assets/Mapicons/house.png';
import ApartmentIconPng from './Assets/Mapicons/apartment.png';
import { Icon } from 'leaflet';
import { makeStyles } from '@mui/styles';
import axios from 'axios';
import Header from './Header';
import { useImmerReducer } from 'use-immer';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
  CardStyle: {
    margin: '0.5rem',
    border: '1px solid black',
    position: 'relative',
    '@media (max-width: 600px)': {
      width: '100%',
    },
  },
  PictureStyle: {
    height: '20rem',
    width: '100%',
    cursor: 'pointer',
    '@media (max-width: 600px)': {
      height: '15rem',
    },
  },
  PriceOverlay: {
    position: 'absolute',
    backgroundColor: 'green',
    zIndex: '1000',
    color: 'white',
    top: '100px',
    left: '20px',
    padding: '5px',
  },
  mapContainer: {
    height: '100vh',
    width: '100%',
  },
  searchContainer: {
    padding: '1rem',
    display: 'flex',
    justifyContent: 'center',
  },
});

function Listings() {
  const navigate = useNavigate();
  const classes = useStyles();
  const listingsRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
   
  const officeIcon = new Icon({
    iconUrl: OfficeIconPng,
    iconSize: [40, 40],
  });
  const houseIcon = new Icon({
    iconUrl: HouseIconPng,
    iconSize: [40, 40],
  });
  const apartmentIcon = new Icon({
    iconUrl: ApartmentIconPng,
    iconSize: [40, 40],
  });

  const [latitude, setLatitude] = useState(9.040082906906534);
  const [longitude, setLongitude] = useState(7.431306154275857);
  const [searchQuery, setSearchQuery] = useState(''); // State for search query

  const initialState = {
    mapInstance: null,
  };

  function ReducerFunction(draft, action) {
    switch (action.type) {
      case "getMap":
        draft.mapInstance = action.mapData;
        break;
    }
  }

  const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

  function TheMapComponent({ dispatch }) {
    const map = useMap();
    useEffect(() => {
      if (map) {
        dispatch({
          type: 'getMap',
          mapData: map,
        });
      }
    }, [map, dispatch]);

    return null;
  }

  function formatPrice(price) {
    if (price >= 1e9) {
      return `₦${(price / 1e9).toFixed(1)}B`;  // Billion
    } else if (price >= 1e6) {
      return `₦${(price / 1e6).toFixed(1)}M`;  // Million
    } else {
      return `₦${price.toString().replace(/\B(?=(\d{3})+(?!d))/g, ",")}`;  // Thousand separator
    }
  }

  const [allListings, setAllListings] = useState([]);
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [pagination, setPagination] = useState({
    next: null,
    previous: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
  
    async function GetAllListings() {
      try {
        const response = await api.get(`${apiUrl}/api/listings/?page=${currentPage}&title=${searchQuery}`, {
          cancelToken: source.token,
          // added from chatgpt
          // withCredentials: false,
        });
  
        console.log(response.data); // Debugging log
        
        // Sort the listings by date
        const sortedListings = response.data.results.sort((a, b) => new Date(b.date_posted) - new Date(a.date_posted));
        
        setAllListings(sortedListings);
        setPagination({
          next: response.data.next,
          previous: response.data.previous,
        });
        setDataIsLoading(false);
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log("Request canceled", error.message);
        } else {
          console.log(error.response);
        }
      }
    }
  
    GetAllListings();
  
    return () => {
      source.cancel("Operation canceled by the user.");
    };
  }, [currentPage, searchQuery]);  // Re-run the effect when currentPage or searchQuery changes

  const handleNextPage = () => {
    if (pagination.next) {
      setCurrentPage((prevPage) => prevPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });  // Scroll to top
    }
  };
  
  const handlePreviousPage = () => {
    if (pagination.previous) {
      setCurrentPage((prevPage) => prevPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });  // Scroll to top
    }
  };

  // Function to handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  if (dataIsLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Header />
      <Grid container>
        <Grid item xs={12} md={4}>
          {/* Search input field */}
          <div className={classes.searchContainer}>
            <TextField
              label="Search Listings"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              fullWidth
            />
          </div>

          {allListings.length > 0 ? (
            allListings.map((listing) => (
              <Card key={listing.id} className={classes.CardStyle}>
                <CardHeader
                  action={
                    <IconButton aria-label="settings" onClick={() => state.mapInstance.flyTo([listing.latitude, listing.longitude], 16)}>
                      <RoomIcon />
                    </IconButton>
                  }
                  title={listing.title}
                />
                <CardMedia
                  className={classes.PictureStyle}
                  component="img"
                  // image={listing.picture1}
                  image={`https://res.cloudinary.com/dvisrqloj/${listing.picture1}`}
                  alt={listing.title}
                  // onClick={() => navigate(`/listing/${listing.id}`)}
                  onClick={() => {
                    console.log(listing.picture1); // Log the image URL
                    navigate(`/listing/${listing.id}`);
                  }}
                />
                <CardContent>
                  <Typography className={classes.PriceOverlay}>
                    {listing.listing_type}: {formatPrice(listing.price)} {listing.property_status === 'Rent' && `/ ${listing.rental_frequency}`}
                  </Typography>
                  <Typography variant="body2">
                    {listing.description.substring(0, 250)}...
                  </Typography>
                </CardContent>
                <CardActions disableSpacing>
                  <IconButton aria-label="add to favorites">
                    {listing.seller_agency_name}
                  </IconButton>
                </CardActions>
              </Card>
            ))
          ) : (
            <Typography variant="body1" style={{ padding: '1rem', textAlign: 'center' }}>
              No listings found.
            </Typography>
          )}

          {/* Pagination Controls */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem' }}>
            <Button variant="contained" disabled={!pagination.previous} onClick={handlePreviousPage}>
              Previous
            </Button>
            <Button variant="contained" disabled={!pagination.next} onClick={handleNextPage}>
              Next
            </Button>
          </div>
        </Grid>

        <Grid item xs={12} md={8} style={{ marginTop: '0.5rem' }}>
          <AppBar position='sticky'>
            <div className={classes.mapContainer}>
              <MapContainer center={[9.0765, 7.3986]} zoom={13} scrollWheelZoom={true}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                />
                <TheMapComponent dispatch={dispatch} />
                {allListings.map((listing) => (
                  <Marker
                    key={listing.id}
                    position={[listing.latitude, listing.longitude]}
                    icon={listing.property_type === 'Office' ? officeIcon : listing.property_type === 'House' ? houseIcon : apartmentIcon}
                  >
                    <Popup>
                      <div>
                        <Typography variant="h6">{listing.title}</Typography>
                        <Typography variant="body2">{formatPrice(listing.price)}</Typography>
                        <Button onClick={() => navigate(`/listing/${listing.id}`)}>View Listing</Button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </AppBar>
        </Grid>
      </Grid>
    </>
  );
}

export default Listings;

// // workssss the bessst please my number 1
// import React, { useState, useEffect, useRef } from 'react';
// import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
// import { Grid, AppBar, Button, Typography, Card, CardContent, CardMedia, IconButton, 
//     CircularProgress, CardActions, CardHeader } from '@mui/material';
// import RoomIcon from '@mui/icons-material/Room';
// import OfficeIconPng from './Assets/Mapicons/office.png';
// import HouseIconPng from './Assets/Mapicons/house.png';
// import ApartmentIconPng from './Assets/Mapicons/apartment.png';
// import { Icon } from 'leaflet';
// import { makeStyles } from '@mui/styles';
// import axios from 'axios';
// import Header from './Header';
// import { useImmerReducer } from 'use-immer';
// import { useNavigate } from 'react-router-dom';

// const useStyles = makeStyles({
//   CardStyle: {
//     margin: '0.5rem',
//     border: '1px solid black',
//     position: 'relative',
//     '@media (max-width: 600px)': {
//       width: '100%',
//     },
//   },
//   PictureStyle: {
//     height: '20rem',
//     width: '100%',
//     cursor: 'pointer',
//     '@media (max-width: 600px)': {
//       height: '15rem',
//     },
//   },
//   PriceOverlay: {
//     position: 'absolute',
//     backgroundColor: 'green',
//     zIndex: '1000',
//     color: 'white',
//     top: '100px',
//     left: '20px',
//     padding: '5px',
//   },
//   mapContainer: {
//     height: '100vh',
//     width: '100%',
//   }
// });

// function Listings() {
//   const navigate = useNavigate();
//   const classes = useStyles();
//   const listingsRef = useRef(null); 
   
//   const officeIcon = new Icon({
//     iconUrl: OfficeIconPng,
//     iconSize: [40, 40],
//   });
//   const houseIcon = new Icon({
//     iconUrl: HouseIconPng,
//     iconSize: [40, 40],
//   });
//   const apartmentIcon = new Icon({
//     iconUrl: ApartmentIconPng,
//     iconSize: [40, 40],
//   });

//   const [latitude, setLatitude] = useState(9.040082906906534);
//   const [longitude, setLongitude] = useState(7.431306154275857);

//   const initialState = {
//     mapInstance: null,
//   };

//   function ReducerFunction(draft, action) {
//     switch (action.type) {
//       case "getMap":
//         draft.mapInstance = action.mapData;
//         break;
//     }
//   }

//   const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

//   function TheMapComponent({ dispatch }) {
//     const map = useMap();
//     useEffect(() => {
//       if (map) {
//         dispatch({
//           type: 'getMap',
//           mapData: map,
//         });
//       }
//     }, [map, dispatch]);

//     return null;
//   }

//   function formatPrice(price) {
//     if (price >= 1e9) {
//       return `₦${(price / 1e9).toFixed(1)}B`;  // Billion
//     } else if (price >= 1e6) {
//       return `₦${(price / 1e6).toFixed(1)}M`;  // Million
//     } else {
//       return `₦${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;  // Thousand separator
//     }
//   }

//   const [allListings, setAllListings] = useState([]);
//   const [dataIsLoading, setDataIsLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1); // Track current page
//   const [pagination, setPagination] = useState({
//     next: null,
//     previous: null,
//   });

//   useEffect(() => {
//     const source = axios.CancelToken.source();
  
//     async function GetAllListings() {
//       try {
//         const response = await axios.get(`http://localhost:8000/api/listings/?page=${currentPage}`, {
//           cancelToken: source.token,
//         });
  
//         console.log(response.data); // Debugging log
        
//         // Sort the listings by date
//         const sortedListings = response.data.results.sort((a, b) => new Date(b.date_posted) - new Date(a.date_posted));
        
//         setAllListings(sortedListings);
//         setPagination({
//           next: response.data.next,
//           previous: response.data.previous,
//         });
//         setDataIsLoading(false);
//       } catch (error) {
//         if (axios.isCancel(error)) {
//           console.log("Request canceled", error.message);
//         } else {
//           console.log(error.response);
//         }
//       }
//     }
  
//     GetAllListings();
  
//     return () => {
//       source.cancel("Operation canceled by the user.");
//     };
//   }, [currentPage]);  // Re-run the effect when currentPage changes

//   // const handleNextPage = () => {
//   //   if (pagination.next) {
//   //     setCurrentPage((prevPage) => prevPage + 1);
//   //   }
//   // };

//   // const handlePreviousPage = () => {
//   //   if (pagination.previous) {
//   //     setCurrentPage((prevPage) => prevPage - 1);
//   //   }
//   // };

//   const handleNextPage = () => {
//     if (pagination.next) {
//       setCurrentPage((prevPage) => prevPage + 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });  // Scroll to top
//     }
//   };
  
//   const handlePreviousPage = () => {
//     if (pagination.previous) {
//       setCurrentPage((prevPage) => prevPage - 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });  // Scroll to top
//     }
//   };  
  
//   if (dataIsLoading) {
//     return (
//       <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//         <CircularProgress />
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header />
//       <Grid container>
//         <Grid item xs={12} md={4}>
//           {allListings.map((listing) => (
//             <Card key={listing.id} className={classes.CardStyle}>
//               <CardHeader
//                 action={
//                   <IconButton aria-label="settings" onClick={() => state.mapInstance.flyTo([listing.latitude, listing.longitude], 16)}>
//                     <RoomIcon />
//                   </IconButton>
//                 }
//                 title={listing.title}
//               />
//               <CardMedia
//                 className={classes.PictureStyle}
//                 component="img"
//                 image={listing.picture1}
//                 alt={listing.title}
//                 onClick={() => navigate(`/listing/${listing.id}`)}
//               />
//               <CardContent>
//                 <Typography className={classes.PriceOverlay}>
//                   {listing.listing_type}: {formatPrice(listing.price)} {listing.property_status === 'Rent' && `/ ${listing.rental_frequency}`}
//                 </Typography>
//                 <Typography variant="body2">
//                   {listing.description.substring(0, 250)}...
//                 </Typography>
//               </CardContent>
//               <CardActions disableSpacing>
//                 <IconButton aria-label="add to favorites">
//                   {listing.seller_agency_name}
//                 </IconButton>
//               </CardActions>
//             </Card>
//           ))}

//           {/* Pagination Controls */}
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem' }}>
//             <Button variant="contained" disabled={!pagination.previous} onClick={handlePreviousPage}>
//               Previous
//             </Button>
//             <Button variant="contained" disabled={!pagination.next} onClick={handleNextPage}>
//               Next
//             </Button>
//           </div>
//         </Grid>

//         <Grid item xs={12} md={8} style={{ marginTop: '0.5rem' }}>
//           <AppBar position='sticky'>
//             <div className={classes.mapContainer}>
//               <MapContainer center={[9.0765, 7.3986]} zoom={13} scrollWheelZoom={true}>
//                 <TileLayer
//                   attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//                   url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//                 />
//                 <TheMapComponent dispatch={dispatch} />
//                 {allListings.map((listing) => {
//                   function IconDisplay() {
//                     if (listing.listing_type === "Office") {
//                       return officeIcon;
//                     } else if (listing.listing_type === "House") {
//                       return houseIcon;
//                     } else if (listing.listing_type === "Apartment") {
//                       return apartmentIcon;
//                     }
//                   }
//                   return (
//                     <Marker key={listing.id} icon={IconDisplay()} position={[listing.latitude, listing.longitude]}>
//                       <Popup>
//                         <Typography variant='h5'>{listing.title}</Typography>
//                         <img src={listing.picture1} alt={listing.title} style={{ height: "14rem", width: "18rem" }} onClick={() => navigate(`/listing/${listing.id}`)} />
//                         <Typography variant='body1'>{listing.description.substring(0, 500)}...</Typography>
//                         <Button variant='contained' onClick={() => navigate(`/listing/${listing.id}`)}>Details</Button>
//                       </Popup>
//                     </Marker>
//                   );
//                 })}
//               </MapContainer>
//             </div>
//           </AppBar>
//         </Grid>
//       </Grid>
//     </>
//   );
// }

// export default Listings;

// works besttttt
// import React, { useState, useEffect } from 'react';
// import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
// import { Grid, AppBar, Button, Typography, Card, CardContent, CardMedia, IconButton, 
//     CircularProgress, CardActions, CardHeader } from '@mui/material';
// import RoomIcon from '@mui/icons-material/Room';
// import OfficeIconPng from './Assets/Mapicons/office.png';
// import HouseIconPng from './Assets/Mapicons/house.png';
// import ApartmentIconPng from './Assets/Mapicons/apartment.png';
// import { Icon } from 'leaflet';
// import { makeStyles } from '@mui/styles';
// import axios from 'axios';
// import Header from './Header';
// import { useImmerReducer } from 'use-immer';
// import { useNavigate } from 'react-router-dom';

// const useStyles = makeStyles({
//   CardStyle: {
//     margin: '0.5rem',
//     border: '1px solid black',
//     position: 'relative',
//     '@media (max-width: 600px)': {
//       width: '100%',
//     },
//   },
//   PictureStyle: {
//     height: '20rem',
//     width: '100%',
//     cursor: 'pointer',
//     '@media (max-width: 600px)': {
//       height: '15rem',
//     },
//   },
//   PriceOverlay: {
//     position: 'absolute',
//     backgroundColor: 'green',
//     zIndex: '1000',
//     color: 'white',
//     top: '100px',
//     left: '20px',
//     padding: '5px',
//   },
//   mapContainer: {
//     height: '100vh',
//     width: '100%',
//   }
// });

// function Listings() {
//   const navigate = useNavigate();
//   const classes = useStyles();
   
//   const officeIcon = new Icon({
//     iconUrl: OfficeIconPng,
//     iconSize: [40, 40],
//   });
//   const houseIcon = new Icon({
//     iconUrl: HouseIconPng,
//     iconSize: [40, 40],
//   });
//   const apartmentIcon = new Icon({
//     iconUrl: ApartmentIconPng,
//     iconSize: [40, 40],
//   });

//   const [latitude, setLatitude] = useState(9.040082906906534);
//   const [longitude, setLongitude] = useState(7.431306154275857);

//   const initialState = {
//     mapInstance: null,
//   };

//   function ReducerFunction(draft, action) {
//     switch (action.type) {
//       case "getMap":
//         draft.mapInstance = action.mapData;
//         break;
//     }
//   }

//   const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

//   function TheMapComponent({ dispatch }) {
//     const map = useMap();
//     useEffect(() => {
//       if (map) {
//         dispatch({
//           type: 'getMap',
//           mapData: map,
//         });
//       }
//     }, [map, dispatch]);

//     return null;
//   }

//   function formatPrice(price) {
//     if (price >= 1e9) {
//       return `₦${(price / 1e9).toFixed(1)}B`;  // Billion
//     } else if (price >= 1e6) {
//       return `₦${(price / 1e6).toFixed(1)}M`;  // Million
//     } else {
//       return `₦${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;  // Thousand separator
//     }
//   }

//   const [allListings, setAllListings] = useState([]);
//   const [dataIsLoading, setDataIsLoading] = useState(true);

//   useEffect(() => {
//     const source = axios.CancelToken.source();
  
//     async function GetAllListings() {
//       try {
//         const response = await axios.get("http://localhost:8000/api/listings/", {
//           cancelToken: source.token,
//         });
  
//         // Log the raw response for debugging
//         console.log(response.data);
  
//         // Access the results array containing the listings
//         const sortedListings = response.data.results.sort((a, b) => new Date(b.date_posted) - new Date(a.date_posted));
        
//         setAllListings(sortedListings);
//         setDataIsLoading(false);
//       } catch (error) {
//         if (axios.isCancel(error)) {
//           console.log("Request canceled", error.message);
//         } else {
//           console.log(error.response);
//         }
//       }
//     }
  
//     GetAllListings();
  
//     return () => {
//       source.cancel("Operation canceled by the user.");
//     };
//   }, []);  

//   if (dataIsLoading) {
//     return (
//       <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//         <CircularProgress />
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header />
//       <Grid container>
//         <Grid item xs={12} md={4}>
//           {allListings.map((listing) => (
//             <Card key={listing.id} className={classes.CardStyle}>
//               <CardHeader
//                 action={
//                   <IconButton aria-label="settings" onClick={() => state.mapInstance.flyTo([listing.latitude, listing.longitude], 16)}>
//                     <RoomIcon />
//                   </IconButton>
//                 }
//                 title={listing.title}
//               />
//               <CardMedia
//                 className={classes.PictureStyle}
//                 component="img"
//                 image={listing.picture1}
//                 alt={listing.title}
//                 onClick={() => navigate(`/listing/${listing.id}`)}
//               />
//               <CardContent>
//                 <Typography className={classes.PriceOverlay}>
//                   {listing.listing_type}: {formatPrice(listing.price)} {listing.property_status === 'Rent' && `/ ${listing.rental_frequency}`}
//                 </Typography>
//                 <Typography variant="body2">
//                   {listing.description.substring(0, 250)}...
//                 </Typography>
//               </CardContent>
//               <CardActions disableSpacing>
//                 <IconButton aria-label="add to favorites">
//                   {listing.seller_agency_name}
//                 </IconButton>
//               </CardActions>
//             </Card>
//           ))}
//         </Grid>
//         <Grid item xs={12} md={8} style={{ marginTop: '0.5rem' }}>
//           <AppBar position='sticky'>
//             <div className={classes.mapContainer}>
//               <MapContainer center={[9.0765, 7.3986]} zoom={13} scrollWheelZoom={true}>
//                 <TileLayer
//                   attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//                   url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//                 />
//                 <TheMapComponent dispatch={dispatch} />
//                 {allListings.map((listing) => {
//                   function IconDisplay() {
//                     if (listing.listing_type === "Office") {
//                       return officeIcon;
//                     } else if (listing.listing_type === "House") {
//                       return houseIcon;
//                     } else if (listing.listing_type === "Apartment") {
//                       return apartmentIcon;
//                     }
//                   }
//                   return (
//                     <Marker key={listing.id} icon={IconDisplay()} position={[listing.latitude, listing.longitude]}>
//                       <Popup>
//                         <Typography variant='h5'>{listing.title}</Typography>
//                         <img src={listing.picture1} alt={listing.title} style={{ height: "14rem", width: "18rem" }} onClick={() => navigate(`/listing/${listing.id}`)} />
//                         <Typography variant='body1'>{listing.description.substring(0, 500)}...</Typography>
//                         <Button variant='contained' fullWidth onClick={() => navigate(`/listing/${listing.id}`)}>Details</Button>
//                       </Popup>
//                     </Marker>
//                   );
//                 })}
//               </MapContainer>
//             </div>
//           </AppBar>
//         </Grid>
//       </Grid>
//     </>
//   );
// }

// export default Listings;

// // works perfectly
// import React,{useState, useEffect} from 'react';
// import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
// import { Grid, AppBar, Button, Typography, Card, CardContent, CardMedia, FavoriteIcon, IconButton, 
//     MoreVertIcon, ShareIcon, red, CardActions, CardHeader, Avatar, CircularProgress } from '@mui/material';
// import OfficeIconPng from './Assets/Mapicons/office.png';
// import HouseIconPng from './Assets/Mapicons/house.png';
// import ApartmentIconPng from './Assets/Mapicons/apartment.png';
// import { Icon } from 'leaflet';
// import myListings from './Assets/Data/Dummydata';
// import { makeStyles } from '@mui/styles';
// import axios from 'axios';
// // import axios from 'axios';
// import Header from './Header';
// import RoomIcon from '@mui/icons-material/Room';
// import { useImmerReducer } from 'use-immer';
// import { useNavigate } from 'react-router-dom';

// const useStyles = makeStyles({
//     CardStyle: {
//         margin: '0.5rem',
//         border: '1px solid black',
//         position: 'relative',
//     },
//     PictureStyle: {
//         paddingRight: '1rem',
//         paddingLeft: '1rem',
//         height: '20rem',
//         width: '30rem',
//         cursor: 'pointer',
//     },
//     PriceOverlay: {
//         position: 'absolute',
//         backgroundColor: 'green',
//         zIndex: '1000',
//         color: 'white',
//         top: '100px',
//         left: '20px',
//         padding: '5px',
//     }
// })

// function Listings() {
//   // fetch("http://localhost:8000/api/listings/")
//   // .then((response) => response.json())
//   // .then((data) => console.log(data));

//   const navigate = useNavigate();
//   const classes = useStyles();
   
//   const officeIcon = new Icon({
//     iconUrl: OfficeIconPng,
//     iconSize: [40, 40],
//   });
//   const houseIcon = new Icon({
//     iconUrl: HouseIconPng,
//     iconSize: [40, 40],
//   });
//   const apartmentIcon = new Icon({
//     iconUrl: ApartmentIconPng,
//     iconSize: [40, 40],
//   });

//   const [latitude, setLatitude] = useState(9.040082906906534);
//   const [longitude, setLongitude] = useState(7.431306154275857);

//   const initialState = {
//     mapInstance: null,
//   };

//   function ReducerFunction(draft, action) {
//     switch (action.type) {
//       case "getMap":
//         draft.mapInstance = action.mapData;
//         break;
//     }
//   }

//   const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

//   function TheMapComponent({ dispatch }) {
//     const map = useMap();

//     useEffect(() => {
//       if (map) {
//         dispatch({
//           type: 'getMap',
//           mapData: map
//         });
//       }
//     }, [map, dispatch]); // Add both map and dispatch as dependencies

//     return null;
//   }

//   function formatPrice(price) {
//     if (price >= 1e9) {
//       return `₦${(price / 1e9).toFixed(1)}B`;  // Billion
//     } else if (price >= 1e6) {
//       return `₦${(price / 1e6).toFixed(1)}M`;  // Million
//     } else {
//       return `₦${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;  // Thousand separator
//     }
//   }

//   function GoEast() {
//     setLatitude(9.040082906906534);
//     setLongitude(7.431306154275857);
//   }

//   // console.log(allListings);
  
//   const [allListings, setAllListings] = useState([]);
//   const [dataIsLoading, setDataIsLoading] = useState(true);

// useEffect(() => {
//   const source = axios.CancelToken.source();  
  
//   async function GetAllListings() {
//     try {
//       const response = await axios.get(
//         "http://localhost:8000/api/listings/",
//         {
//         cancelToken: source.token
//         }
//       );
//       const sortedListings = response.data.sort((a, b) => new Date(b.date_posted) - new Date(a.date_posted));
//       setAllListings(sortedListings);
//       // setAllListings(response.data);
//       setDataIsLoading(false);
//     } catch (error) {
//       if (axios.isCancel(error)) {
//         console.log("Request canceled", error.message);
//       } else {
//         console.log(error.response);
//       }
//     }
//   }

//   GetAllListings();

//   return () => {
//     source.cancel("Operation canceled by the user.");
//   };
// }, []);


// if (dataIsLoading === true) {
//   return(
//     <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//       <CircularProgress />
//     </div>
//   )
// }

// if (allListings && allListings.length > 0) {
//   const location = {
//     lat: allListings[0].latitude,
//     lng: allListings[0].longitude,
//   };
//   console.log(location);
// }


//   return (
//     <>
//     <Header/>
//     <Grid container>
//       <Grid item xs={4}>
//         {allListings.map((listing)=> {
//             return(
//                 <Card key={listing.id} className={classes.CardStyle}>
//                     <CardHeader
//                         action={
//                             <IconButton aria-label="settings" onClick={() => state.mapInstance.flyTo([listing.latitude, listing.longitude], 16)}>
//                             {/* <MoreVertIcon /> */}
//                             <RoomIcon />
//                             </IconButton>
//                         }
//                         title={listing.title}
//                     />
//                     <CardMedia className={classes.PictureStyle}
//                         component="img"
//                         image={listing.picture1}
//                         alt={listing.title}
//                         onClick={() => navigate(`/listing/${listing.id}`)}
//                     />
//                     <CardContent>
//                       {listing.property_status === 'Sale' ? (
//                         <Typography className={classes.PriceOverlay}>
//                             {listing.listing_type}: {formatPrice(listing.price)}
//                         </Typography>
//                       ) : (
//                         <Typography className={classes.PriceOverlay}>
//                             {listing.listing_type}: {formatPrice(listing.price)} / {listing.rental_frequency}
//                         </Typography>
//                       )}
//                       <Typography variant="body2">
//                         {listing.description.substring(0, 250)}...
//                       </Typography>
//                     </CardContent>
//                     <CardActions disableSpacing>
//                         <IconButton aria-label="add to favorites">
//                         {/* <FavoriteIcon /> */}
//                         {/* {listing.seller_full_name} */}
//                         {listing.seller_agency_name}
//                         </IconButton>
//                         {/* <IconButton aria-label="share">
//                         <ShareIcon />
//                         </IconButton> */}
//                     </CardActions>
//                 </Card>
//             );
//         })}
//       </Grid>
//       <Grid item xs={8} style={{marginTop: '0.5rem'}}>
//         <AppBar position='sticky'>
//           <div style={{ height: "100vh" }}>
//             <MapContainer center={[9.0765, 7.3986]} zoom={13} scrollWheelZoom={true}>
//               <TileLayer
//                 attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//                 url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//               />
//               <TheMapComponent dispatch={dispatch} />
//               {allListings.map((listing) => {
//                 function IconDisplay() {
//                   if (listing.listing_type === "Office") {
//                     return officeIcon;
//                   } else if (listing.listing_type === "House") {
//                     return houseIcon;
//                   } else if (listing.listing_type === "Apartment") { // Corrected typo here
//                     return apartmentIcon;
//                   }
//                 }
//                 return (
//                   <Marker key={listing.id} icon={IconDisplay()} position={[listing.latitude, listing.longitude]}>
//                     <Popup>
//                       <Typography variant='h5'>{listing.title}</Typography>
//                       <img src={listing.picture1} 
//                       alt={listing.title} style={{ height: "14rem", width: "18rem" }}
//                       onClick={() => navigate(`/listing/${listing.id}`)}
//                       />
//                       <Typography variant='body1'>{listing.description.substring(0, 500)}...</Typography>
//                       <Button variant='contained' fullWidth 
//                       onClick={() => navigate(`/listing/${listing.id}`)}>
//                         Details
//                       </Button>
//                     </Popup>
//                   </Marker>
//                 );              
//               })}
//             </MapContainer>
//           </div>
//         </AppBar>
//       </Grid>
//     </Grid>
//     </>
//   );
// }

// export default Listings;

// import React,{useState, useEffect} from 'react';
// import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
// import { Grid, AppBar, Button, Typography, Card, CardContent, CardMedia, IconButton, 
//   CardHeader, CardActions, CircularProgress } from '@mui/material';
// import OfficeIconPng from './Assets/Mapicons/office.png';
// import HouseIconPng from './Assets/Mapicons/house.png';
// import ApartmentIconPng from './Assets/Mapicons/apartment.png';
// import { Icon } from 'leaflet';
// import { makeStyles } from '@mui/styles';
// import axios from 'axios'; // Correctly importing axios
// import Header from './Header';
// import RoomIcon from '@mui/icons-material/Room';
// import { useImmerReducer } from 'use-immer';
// import { useNavigate } from 'react-router-dom';

// const useStyles = makeStyles({
//     CardStyle: {
//         margin: '0.5rem',
//         border: '1px solid black',
//         position: 'relative',
//     },
//     PictureStyle: {
//         paddingRight: '1rem',
//         paddingLeft: '1rem',
//         height: '20rem',
//         width: '30rem',
//         cursor: 'pointer',
//     },
//     PriceOverlay: {
//         position: 'absolute',
//         backgroundColor: 'green',
//         zIndex: '1000',
//         color: 'white',
//         top: '100px',
//         left: '20px',
//         padding: '5px',
//     }
// })

// function Listings() {
//   const navigate = useNavigate();
//   const classes = useStyles();
   
//   const officeIcon = new Icon({
//     iconUrl: OfficeIconPng,
//     iconSize: [40, 40],
//   });
//   const houseIcon = new Icon({
//     iconUrl: HouseIconPng,
//     iconSize: [40, 40],
//   });
//   const apartmentIcon = new Icon({
//     iconUrl: ApartmentIconPng,
//     iconSize: [40, 40],
//   });

//   const [latitude, setLatitude] = useState(9.040082906906534);
//   const [longitude, setLongitude] = useState(7.431306154275857);

//   const initialState = {
//     mapInstance: null,
//   };

//   function ReducerFunction(draft, action) {
//     switch (action.type) {
//       case "getMap":
//         draft.mapInstance = action.mapData;
//         break;
//     }
//   }

//   const [state, dispatch] = useImmerReducer(ReducerFunction, initialState);

//   function TheMapComponent({ dispatch }) {
//     const map = useMap();

//     useEffect(() => {
//       if (map) {
//         dispatch({
//           type: 'getMap',
//           mapData: map
//         });
//       }
//     }, [map, dispatch]);

//     return null;
//   }

//   function formatPrice(price) {
//     if (price >= 1e9) {
//       return `₦${(price / 1e9).toFixed(1)}B`;  // Billion
//     } else if (price >= 1e6) {
//       return `₦${(price / 1e6).toFixed(1)}M`;  // Million
//     } else {
//       return `₦${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;  // Thousand separator
//     }
//   }

//   const [allListings, setAllListings] = useState([]);
//   const [dataIsLoading, setDataIsLoading] = useState(true);

//   useEffect(() => {
//     const source = axios.CancelToken.source();  // Using correct axios import
    
//     async function GetAllListings() {
//       try {
//         const response = await axios.get(
//           "http://localhost:8000/api/listings/",
//           {
//             cancelToken: source.token
//           }
//         );
//         const sortedListings = response.data.sort((a, b) => new Date(b.date_posted) - new Date(a.date_posted));
//         setAllListings(sortedListings);
//         setDataIsLoading(false);
//       } catch (error) {
//         if (axios.isCancel(error)) {
//           console.log("Request canceled", error.message);
//         } else {
//           console.log(error.response);
//         }
//       }
//     }

//     GetAllListings();

//     return () => {
//       source.cancel("Operation canceled by the user.");
//     };
//   }, []);

//   if (dataIsLoading === true) {
//     return (
//       <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//         <CircularProgress />
//       </div>
//     );
//   }

//   return (
//     <>
//     <Header/>
//     <Grid container>
//       <Grid item xs={4}>
//         {allListings.map((listing)=> {
//             return(
//                 <Card key={listing.id} className={classes.CardStyle}>
//                     <CardHeader
//                         action={
//                             <IconButton aria-label="settings" onClick={() => state.mapInstance.flyTo([listing.latitude, listing.longitude], 16)}>
//                             <RoomIcon />
//                             </IconButton>
//                         }
//                         title={listing.title}
//                     />
//                     <CardMedia className={classes.PictureStyle}
//                         component="img"
//                         image={listing.picture1}
//                         alt={listing.title}
//                         onClick={() => navigate(`/listing/${listing.id}`)}
//                     />
//                     <CardContent>
//                       {listing.property_status === 'Sale' ? (
//                         <Typography className={classes.PriceOverlay}>
//                             {listing.listing_type}: {formatPrice(listing.price)}
//                         </Typography>
//                       ) : (
//                         <Typography className={classes.PriceOverlay}>
//                             {listing.listing_type}: {formatPrice(listing.price)} / {listing.rental_frequency}
//                         </Typography>
//                       )}
//                       <Typography variant="body2">
//                         {listing.description.substring(0, 250)}...
//                       </Typography>
//                     </CardContent>
//                     <CardActions disableSpacing>
//                         <IconButton aria-label="add to favorites">
//                         {listing.seller_agency_name}
//                         </IconButton>
//                     </CardActions>
//                 </Card>
//             );
//         })}
//       </Grid>
//       <Grid item xs={8} style={{marginTop: '0.5rem'}}>
//         <AppBar position='sticky'>
//           <div style={{ height: "100vh" }}>
//             <MapContainer center={[9.0765, 7.3986]} zoom={13} scrollWheelZoom={true}>
//               <TileLayer
//                 attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
//                 url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//               />
//               <TheMapComponent dispatch={dispatch} />
//               {allListings.map((listing) => {
//                 function IconDisplay() {
//                   if (listing.listing_type === "Office") {
//                     return officeIcon;
//                   } else if (listing.listing_type === "House") {
//                     return houseIcon;
//                   } else if (listing.listing_type === "Apartment") {
//                     return apartmentIcon;
//                   }
//                 }
//                 return (
//                   <Marker key={listing.id} icon={IconDisplay()} position={[listing.latitude, listing.longitude]}>
//                     <Popup>
//                       <Typography variant='h5'>{listing.title}</Typography>
//                       <img src={listing.picture1} 
//                       alt={listing.title} style={{ height: "14rem", width: "18rem" }}
//                       onClick={() => navigate(`/listing/${listing.id}`)}
//                       />
//                       <Typography variant='body1'>{listing.description.substring(0, 500)}...</Typography>
//                       <Button variant='contained' fullWidth 
//                       onClick={() => navigate(`/listing/${listing.id}`)}>
//                         Details
//                       </Button>
//                     </Popup>
//                   </Marker>
//                 );              
//               })}
//             </MapContainer>
//           </div>
//         </AppBar>
//       </Grid>
//     </Grid>
//     </>
//   );
// }

// export default Listings;
